/**
 * GLB Thumbnail Generator
 *
 * Uses Babylon.js to render a thumbnail from a GLB 3D model file.
 * Creates an offscreen canvas, loads the model, renders one frame,
 * and returns a PNG blob suitable for upload.
 */

import { Engine, Scene, ArcRotateCamera, HemisphericLight, Vector3, Color4, SceneLoader } from "@babylonjs/core";
import "@babylonjs/loaders/glTF";

interface ThumbnailOptions {
  width?: number;
  height?: number;
  backgroundColor?: string;
}

const DEFAULT_OPTIONS: Required<ThumbnailOptions> = {
  width: 512,
  height: 512,
  backgroundColor: "#f5f5f5",
};

/**
 * Generate a thumbnail PNG from a GLB file
 *
 * @param glbFile - The GLB file to render
 * @param options - Thumbnail generation options
 * @returns Promise<Blob> - PNG blob of the rendered thumbnail
 */
export async function generateThumbnailFromGlb(
  glbFile: File,
  options: ThumbnailOptions = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Create offscreen canvas
  const canvas = document.createElement("canvas");
  canvas.width = opts.width;
  canvas.height = opts.height;
  canvas.style.display = "none";
  document.body.appendChild(canvas);

  try {
    // Initialize Babylon.js engine
    const engine = new Engine(canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
    });

    // Create scene with background color
    const scene = new Scene(engine);
    scene.clearColor = hexToColor4(opts.backgroundColor);

    // Add lighting
    const light = new HemisphericLight("light", new Vector3(0, 1, 0), scene);
    light.intensity = 1.2;

    // Add secondary fill light from below
    const fillLight = new HemisphericLight("fillLight", new Vector3(0, -0.5, 0), scene);
    fillLight.intensity = 0.4;

    // Load the GLB file
    const fileUrl = URL.createObjectURL(glbFile);

    try {
      const result = await SceneLoader.ImportMeshAsync(
        "",
        "",
        fileUrl,
        scene,
        undefined,
        ".glb"
      );

      // Calculate bounding box for all meshes
      let minX = Infinity, minY = Infinity, minZ = Infinity;
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

      result.meshes.forEach((mesh) => {
        mesh.computeWorldMatrix(true);
        const boundingInfo = mesh.getBoundingInfo();
        if (boundingInfo) {
          const min = boundingInfo.boundingBox.minimumWorld;
          const max = boundingInfo.boundingBox.maximumWorld;
          minX = Math.min(minX, min.x);
          minY = Math.min(minY, min.y);
          minZ = Math.min(minZ, min.z);
          maxX = Math.max(maxX, max.x);
          maxY = Math.max(maxY, max.y);
          maxZ = Math.max(maxZ, max.z);
        }
      });

      // Calculate center and size
      const center = new Vector3(
        (minX + maxX) / 2,
        (minY + maxY) / 2,
        (minZ + maxZ) / 2
      );
      const size = Math.max(maxX - minX, maxY - minY, maxZ - minZ) || 1;

      // Create camera with optimal view
      const camera = new ArcRotateCamera(
        "camera",
        Math.PI / 4,       // alpha - horizontal rotation (45 degrees)
        Math.PI / 3,       // beta - vertical rotation (60 degrees from top)
        size * 2.5,        // radius - distance from target
        center,
        scene
      );
      camera.attachControl(canvas, false);

      // Wait for scene to be ready before rendering
      await scene.whenReadyAsync();

      // Render one frame
      scene.render();

      // Capture as PNG
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to create thumbnail blob"));
            }
          },
          "image/png",
          0.9
        );
      });

      return blob;
    } finally {
      URL.revokeObjectURL(fileUrl);
      // Dispose scene first, then engine (correct order)
      scene.dispose();
      engine.dispose();
    }
  } finally {
    document.body.removeChild(canvas);
  }
}

/**
 * Convert hex color string to Babylon.js Color4
 */
function hexToColor4(hex: string): Color4 {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return new Color4(
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255,
      1
    );
  }
  return new Color4(0.96, 0.96, 0.96, 1); // Default light gray
}

/**
 * Create a File object from a Blob with a filename
 */
export function blobToFile(blob: Blob, filename: string): File {
  return new File([blob], filename, { type: blob.type });
}
